import { useMemo, useState } from 'react';
import { useCatalog, saveCatalog, type Catalog, type Material, type Die } from '../lib/csrCatalog';

type TabKey = 'materials' | 'dies' | 'coatings' | 'adhesives';
type Item = Material | Die | string;

interface FieldDef { k: string; label: string; num?: boolean; full?: boolean; }
interface TabDef {
  label: string; kind: 'obj' | 'str';
  fields?: FieldDef[];
  name: (it: Item) => string;
  meta: (it: Item) => string;
  match: (it: Item, q: string) => boolean;
  blank: () => Item;
}

const TABS: Record<TabKey, TabDef> = {
  materials: {
    label: 'Face Stocks / Materials', kind: 'obj',
    fields: [
      { k: 'name', label: 'Name', full: true },
      { k: 'liner', label: 'Liner' }, { k: 'adhesive', label: 'Adhesive' },
      { k: 'msi', label: 'MSI Cost ($)', num: true },
    ],
    name: (it) => (it as Material).name || 'Material',
    meta: (it) => { const m = it as Material; return [m.liner && m.liner !== 'None' ? `Liner ${m.liner}` : '', m.adhesive && m.adhesive !== 'None' ? m.adhesive : '', m.msi != null ? `$${m.msi}/msi` : ''].filter(Boolean).join(' · '); },
    match: (it, q) => { const m = it as Material; return `${m.name || ''} ${m.liner || ''} ${m.adhesive || ''}`.toLowerCase().includes(q); },
    blank: () => ({ name: 'NEW MATERIAL', liner: 'None', adhesive: 'None', msi: 0 }),
  },
  dies: {
    label: 'Dies', kind: 'obj',
    fields: [
      { k: 'die', label: 'Die #' }, { k: 'shape', label: 'Shape' }, { k: 'blank', label: 'Blank Size' },
      { k: 'across', label: 'Size Across', num: true }, { k: 'around', label: 'Size Around', num: true },
      { k: 'repeat', label: 'Repeat', num: true }, { k: 'nAcross', label: '# Across', num: true },
      { k: 'nAround', label: '# Around', num: true }, { k: 'gapAround', label: 'Gap Around', num: true },
      { k: 'gapAcross', label: 'Gap Across', num: true }, { k: 'radius', label: 'Corner Radius', num: true },
      { k: 'notes', label: 'Notes', full: true },
    ],
    name: (it) => { const d = it as Die; return `${d.die || 'DIE'} — ${d.shape || ''}`; },
    meta: (it) => { const d = it as Die; return `${d.across ?? '?'}×${d.around ?? '?'} · ${d.nAcross ?? '?'} up${d.notes ? ' · ' + d.notes : ''}`; },
    match: (it, q) => { const d = it as Die; return `${d.die || ''} ${d.shape || ''} ${d.notes || ''}`.toLowerCase().includes(q); },
    blank: () => ({ die: 'NEW-DIE', shape: 'Rectangle', blank: '', across: 0, around: 0, repeat: 0, nAcross: 1, nAround: 1, gapAround: 0, gapAcross: 0, radius: 0, notes: '' }),
  },
  coatings: {
    label: 'Coatings / Varnishes', kind: 'str',
    name: (it) => String(it), meta: () => '', match: (it, q) => String(it).toLowerCase().includes(q), blank: () => 'New Coating',
  },
  adhesives: {
    label: 'Adhesives', kind: 'str',
    name: (it) => String(it), meta: () => '', match: (it, q) => String(it).toLowerCase().includes(q), blank: () => 'New Adhesive',
  },
};

export default function MaterialsDies() {
  const { catalog, loading, live, error } = useCatalog();
  const [tab, setTab] = useState<TabKey>('materials');
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(-1);
  const [draft, setDraft] = useState<Item | null>(null);
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [msg, setMsg] = useState('');

  const def = TABS[tab];
  const arr = catalog[tab] as Item[];

  const shown = useMemo(() => {
    const t = q.trim().toLowerCase();
    return arr.map((it, i) => ({ it, i })).filter(({ it }) => !t || def.match(it, t));
  }, [arr, q, def]);

  const pick = (i: number) => {
    setSel(i); setStatus('idle');
    const it = arr[i];
    setDraft(typeof it === 'string' ? it : { ...(it as Material | Die) });
  };
  const switchTab = (k: TabKey) => { setTab(k); setSel(-1); setDraft(null); setQ(''); setStatus('idle'); };

  async function persist(next: Item[], note: string) {
    setStatus('saving');
    try {
      const full: Catalog = { ...catalog, [tab]: next } as Catalog;
      await saveCatalog(full);
      setStatus('saved'); setMsg(note);
      setTimeout(() => setStatus('idle'), 1600);
    } catch (e) {
      setStatus('error');
      setMsg((e as Error)?.message?.includes('permission') ? 'Sign in with your Microflex account to save.' : ((e as Error)?.message || 'Save failed'));
    }
  }

  async function onSave() {
    if (draft == null || sel < 0) return;
    const next = arr.map((it, i) => (i === sel ? draft : it));
    await persist(next, 'Saved');
  }
  async function onAdd() {
    const fresh = def.blank();
    setQ('');
    const next = [fresh, ...arr];
    setSel(0); setDraft(typeof fresh === 'string' ? fresh : { ...(fresh as Material | Die) });
    await persist(next, 'Added');
  }
  async function onDelete() {
    if (sel < 0) return;
    if (!window.confirm(`Delete "${def.name(arr[sel])}" from ${def.label}?`)) return;
    const next = arr.filter((_, i) => i !== sel);
    setSel(-1); setDraft(null);
    await persist(next, 'Deleted');
  }

  const setField = (k: string, num?: boolean) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setDraft((p) => (p == null || typeof p === 'string' ? p : { ...p, [k]: num ? (parseFloat(e.target.value) || 0) : e.target.value }));

  return (
    <>
      <style>{css}</style>
      <div className="page-head">
        <div>
          <div className="page-title">Materials &amp; Dies</div>
          <div className="page-sub">The estimating suite's shared catalog — items appear instantly in the quote pickers. {live ? 'Live' : loading ? 'Loading…' : 'Offline'}.</div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn small md-btn" onClick={onAdd}>＋ Add {tab === 'dies' ? 'Die' : tab === 'materials' ? 'Material' : def.label.replace(/s( \/.*)?$/, '')}</button>
        </div>
      </div>

      {error && <div className="md-warn">Couldn’t load the catalog: {error}</div>}

      <div className="md-tabs">
        {(Object.keys(TABS) as TabKey[]).map((k) => (
          <button key={k} className={tab === k ? 'on' : ''} onClick={() => switchTab(k)}>{TABS[k].label} <span>{(catalog[k] as Item[]).length}</span></button>
        ))}
      </div>

      <div className="md-grid">
        <div className="card md-list">
          <input placeholder={`🔎 Search ${def.label.toLowerCase()}…`} value={q} onChange={(e) => setQ(e.target.value)} style={{ marginBottom: 10 }} />
          <div className="md-rows">
            {loading ? <div className="md-empty">Loading…</div> : shown.length === 0 ? <div className="md-empty">No matches</div> :
              shown.map(({ it, i }) => (
                <div key={i} className={`md-row${i === sel ? ' active' : ''}`} onClick={() => pick(i)}>
                  <div className="md-nm">{def.name(it)}</div>
                  {def.meta(it) && <div className="md-meta">{def.meta(it)}</div>}
                </div>
              ))}
          </div>
        </div>

        <div className="card md-detail">
          {draft == null ? (
            <div className="md-empty big">🗂️<div>Select an item to edit,<br />or add a new one.</div></div>
          ) : (
            <>
              <div className="md-dh">
                <h2>{typeof draft === 'string' ? `Edit ${def.label.replace(/s( \/.*)?$/, '')}` : def.name(draft)}</h2>
                <div className={`md-status ${status}`}>{status === 'saving' ? 'Saving…' : status === 'saved' ? `✔ ${msg}` : status === 'error' ? `⚠ ${msg}` : ''}</div>
              </div>
              {typeof draft === 'string' ? (
                <label className="md-f wide"><span>Value — appears in the quote dropdown</span>
                  <input value={draft} onChange={(e) => setDraft(e.target.value)} /></label>
              ) : (
                <div className="md-fields">
                  {def.fields!.map((f) => (
                    <label key={f.k} className={`md-f${f.full ? ' wide' : ''}`}><span>{f.label}</span>
                      <input type={f.num ? 'number' : 'text'} step={f.num ? 'any' : undefined}
                        value={String((draft as Material | Die)[f.k] ?? (f.num ? 0 : ''))} onChange={setField(f.k, f.num)} />
                    </label>
                  ))}
                </div>
              )}
              <div className="md-savebar">
                <button className="btn small primary md-btn" onClick={onSave} disabled={status === 'saving'}>💾 Save</button>
                <button className="btn small md-btn danger" style={{ marginLeft: 'auto' }} onClick={onDelete}>🗑 Delete</button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

const css = `
.md-btn{width:auto;padding:8px 14px;font-size:13px}
.md-warn{background:rgba(234,179,8,.12);border:1px solid rgba(234,179,8,.4);color:var(--yl);border-radius:10px;padding:10px 14px;font-size:13px;margin:10px 0}
.md-tabs{display:flex;gap:6px;flex-wrap:wrap;margin:12px 0}
.md-tabs button{background:var(--bg2);color:var(--tx2);border:1px solid var(--bdr);border-radius:999px;padding:7px 14px;font-size:12px;font-weight:600;cursor:pointer}
.md-tabs button.on{background:rgba(0,168,207,.14);border-color:rgba(0,168,207,.45);color:var(--ac)}
.md-tabs button span{opacity:.6;margin-left:4px}
.md-grid{display:grid;grid-template-columns:340px 1fr;gap:16px;align-items:start}
@media(max-width:960px){.md-grid{grid-template-columns:1fr}}
.md-list{padding:12px}
.md-rows{max-height:70vh;overflow:auto}
.md-row{padding:9px 12px;border-radius:8px;cursor:pointer;border:1px solid transparent}
.md-row:hover{background:var(--bg2)}
.md-row.active{background:rgba(0,168,207,.12);border-color:rgba(0,168,207,.4)}
.md-nm{font-weight:700;font-size:12.5px;color:var(--tx)}
.md-meta{font-size:11px;color:var(--tx3);margin-top:2px}
.md-detail{padding:18px;min-height:300px}
.md-empty{color:var(--tx3);text-align:center;padding:30px;font-size:13px}
.md-empty.big{font-size:34px;display:flex;flex-direction:column;align-items:center;gap:12px}
.md-empty.big div{font-size:13px}
.md-dh{display:flex;align-items:center;margin-bottom:14px}
.md-dh h2{margin:0;font-size:17px}
.md-status{margin-left:auto;font-size:12px;font-weight:600}
.md-status.saved{color:var(--gn)}.md-status.error{color:var(--rd)}.md-status.saving{color:var(--ac)}
.md-fields{display:grid;grid-template-columns:1fr 1fr 1fr;gap:10px 14px}
@media(max-width:680px){.md-fields{grid-template-columns:1fr 1fr}}
.md-f{display:flex;flex-direction:column;gap:5px}
.md-f.wide{grid-column:1/-1}
.md-f>span{font-size:11px;color:var(--tx3);font-weight:600}
.md-savebar{display:flex;align-items:center;gap:10px;border-top:1px solid var(--bdr);padding-top:14px;margin-top:16px}
.md-btn.danger{color:var(--rd);border-color:rgba(239,68,68,.4)}
`;
